import React, {
    FunctionComponent,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styles from './TargetGroupView.module.css';
import AppButton from 'components/AppButton/AppButton';
import ProgressSpinner from 'components/ProgressSpinner/ProgressSpinner';
import TargetGroupTable from 'views/TargetGroupView/TargetGroupTable/TargetGroupTable';
import TargetGroupModals from './TargetGroupModals/TargetGroupModals';
import { AuthenticatedUserProps } from '../../hoc/WithAuthLayout';
import TargetGroupScan from './TargetGroupScan/TargetGroupScan';
import { getWorkPreferencesFormattedData } from './utils';
import WorkPreferenceBaseLineBarChart from '../../components/WorkPreferenceBaseLineBarChart';
import { useGetCompany } from '../../store/hooks/useCompany';
import GroupHeader from './GroupHeader/GroupHeader';
import {
    useBatchProcessParticipantMutation,
    useGetParticipantsQuery,
    useSendParticipantEmailsMutation,
    useSendResetPasswordEmailsMutation,
} from '../../store/api/participants';
import {
    useDeleteGroupMutation,
    useGetGroupQuery,
    useGetGroupWorkPreferencesBaselineQuery,
} from '../../store/api/groups';
import { useGetScanTemplateQuery } from '../../store/api/scanTemplates';
import TargetGroupTableFooter from './TargetGroupTable/TargetGroupTableFooter/TargetGroupTableFooter';
import { Participant } from '../../typings/participant';
import SearchInput from '../SearchView/SearchInput/SearchInput';

interface TargetGroupViewProps extends AuthenticatedUserProps {}

const TargetGroupView: FunctionComponent<TargetGroupViewProps> = (props) => {
    const { userRoles, hasMultipleCompanies } = props;
    const { companySlug, groupId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // State
    const [shouldShowInviteModal, showInviteModal] = useState<boolean>(false);
    const [shouldShowRemoveModal, showRemoveModal] = useState<boolean>(false);
    const [shouldShowFullDeleteCandidateModal, showFullDeleteCandidateModal] =
        useState<boolean>(false);
    const [shouldShowResetCandidateScanModal, showResetCandidateScanModal] =
        useState<boolean>(false);
    const [
        shouldShowResetCandidateScanSuccessModal,
        setResetCandidateScanSuccessModal,
    ] = useState<boolean>(false);
    const [shouldShowDeleteTargetGroupModal, showDeleteTargetGroupModal] =
        useState<boolean>(false);
    const [shouldShowReminderModal, showReminderModal] =
        useState<boolean>(false);
    const [shouldShowResetPasswordModal, showResetPasswordModal] =
        useState<boolean>(false);
    const [shouldShowReportModal, showReportModal] = useState<boolean>(false);
    const [participantsSelected, setSelectedParticipants] = useState<
        Participant[]
    >([]);

    // Pagination
    const perPage = 50;
    const page = searchParams.get('page');
    const currentPage = Number(page) > 1 ? Number(page) : 1;

    // RTK Queries
    const { company } = useGetCompany(companySlug);

    const { data: group } = useGetGroupQuery({
        companyIdentifier: companySlug as string,
        groupId: groupId as string,
    });

    const { data: scanTemplate } = useGetScanTemplateQuery(
        {
            companyIdentifier: companySlug as string,
            scanTemplateId: group?.assessment_id as string,
        },
        {
            skip: !group,
        },
    );

    const {
        data: companyWorkPreferencesBaseline,
        isFetching: isFetchingWorkPreferencesBaseline,
    } = useGetGroupWorkPreferencesBaselineQuery({
        companyIdentifier: companySlug as string,
        groupId: groupId as string,
    });

    const { data: participantsData, isFetching: isFetchingParticipants } =
        useGetParticipantsQuery({
            companyIdentifier: companySlug as string,
            groupId: groupId as string,
            limit: perPage,
            includeTotal: true,
            includeResults: true,
            page: currentPage,
        });

    const [
        batchProcessParticipant,
        { isLoading: isBatchProcessLoading, originalArgs },
    ] = useBatchProcessParticipantMutation();

    const [sendParticipantEmails] = useSendParticipantEmailsMutation();

    const [sendResetPasswordEmails] = useSendResetPasswordEmailsMutation();

    const [deleteGroup] = useDeleteGroupMutation();

    // Misc.
    const isSuperAdmin = userRoles.includes('super-admin');
    const formattedWorkPreferencesBaseline = useMemo(() => {
        return getWorkPreferencesFormattedData(companyWorkPreferencesBaseline);
    }, [companyWorkPreferencesBaseline]);

    const getParticipantIdsToBatchProcess = useCallback(
        (
            operation:
                | 'delete'
                | 'full_delete'
                | 'reset'
                | 'anonymize'
                | undefined,
        ): string[] => {
            if (operation === 'delete') {
                return [
                    ...[...participantsSelected].filter(
                        (participant) => !participant.analysis,
                    ),
                ].map((participant) => participant.user_id);
            } else {
                return [...participantsSelected].map(
                    (participant) => participant.user_id,
                );
            }
        },
        [participantsSelected],
    );

    if (!company || !group || !scanTemplate) {
        return <ProgressSpinner />;
    }

    const deleteEmptyTargetGroup = () => {
        deleteGroup({
            companyIdentifier: company.id,
            groupId: group.id,
        })
            .unwrap()
            .then(() => {
                navigate(`/${company.slug}`);
            });
    };

    const onUserSelectChange = (userId: string, event: any) => {
        if (event.target.checked === true) {
            const selected = [...participantsSelected];
            const participant = participantsData?.participants.find(
                (p) => p.user_id === userId,
            );
            if (participant) {
                selected.push(participant);
                setSelectedParticipants(selected);
            }
        } else if (event.target.checked === false) {
            const selected = [...participantsSelected].filter(
                (participant) => participant.user_id !== userId,
            );
            setSelectedParticipants(selected);
        }
    };

    const onUserSelectChangeAll = () => {
        if (
            participantsData &&
            participantsData.participants.length !== participantsSelected.length
        ) {
            setSelectedParticipants(participantsData.participants);
        } else {
            setSelectedParticipants([]);
        }
    };

    const discardModals = () => {
        showInviteModal(false);
        showResetPasswordModal(false);
        showReminderModal(false);
        showReportModal(false);
        showRemoveModal(false);
        showFullDeleteCandidateModal(false);
        showResetCandidateScanModal(false);
        showDeleteTargetGroupModal(false);
        setResetCandidateScanSuccessModal(false);
    };

    const sendParticipantsResetPasswordEmails = () => {
        const userIds = [...participantsSelected].map(
            (participant) => participant.user_id,
        );
        sendResetPasswordEmails({
            groupId: group.id,
            userIds: userIds,
        });
        discardModals();
    };

    const sendReminderEmails = () => {
        const userIds = [...participantsSelected].map(
            (participant) => participant.user_id,
        );
        sendParticipantEmails({
            companyIdentifier: company.id,
            groupId: group.id,
            emailType: 'reminder',
            userIds: userIds,
            resend: true,
        });
        discardModals();
    };

    const selectedParticipantsThatHaveReport = (): Participant[] => {
        return [...participantsSelected].filter(
            (participant) => !!participant.report,
        );
    };

    const sendReportEmails = () => {
        const participantsWithReport = selectedParticipantsThatHaveReport();
        const userIds = [...participantsWithReport].map(
            (participant) => participant.user_id,
        );

        sendParticipantEmails({
            companyIdentifier: company.id,
            groupId: group.id,
            emailType: 'report',
            userIds: userIds,
            resend: true,
        });
        discardModals();
    };

    const removeCandidates = () => {
        batchProcessParticipant({
            companyIdentifier: company.id,
            groupId: group.id,
            operation: 'delete',
            userIds: getParticipantIdsToBatchProcess('delete'),
        });

        const newSelected = [...participantsSelected].filter((selected) => {
            return !participantsData?.participants.find(
                (participant) => participant.user_id === selected.user_id,
            );
        });

        setSelectedParticipants(newSelected);
        discardModals();
        setSearchParams({ page: '1' });
    };

    const fullDeleteCandidates = () => {
        batchProcessParticipant({
            companyIdentifier: company.id,
            groupId: group.id,
            operation: 'full_delete',
            userIds: getParticipantIdsToBatchProcess('full_delete'),
        });

        const newSelected = [...participantsSelected].filter((selected) => {
            return !participantsData?.participants.find(
                (participant) => participant.user_id === selected.user_id,
            );
        });

        setSelectedParticipants(newSelected);
        discardModals();
        setSearchParams({ page: '1' });
    };

    const resetCandidateScan = () => {
        batchProcessParticipant({
            companyIdentifier: company.id,
            groupId: group.id,
            operation: 'reset',
            userIds: getParticipantIdsToBatchProcess('reset'),
        });
        discardModals();
    };

    return (
        <div className={styles.target_group_view}>
            <GroupHeader
                isSuperAdmin={isSuperAdmin}
                hasMultipleCompanies={hasMultipleCompanies}
                groupName={group.name}
                companyName={company.name}
                companySlug={company.slug}
                participantsCount={group.participants_count}
                groupType={group.type}
                scanTemplateId={scanTemplate.id}
                scanTemplateName={scanTemplate.name}
                groupId={group.id}
                companyId={company.id}
                scanTemplateLanguage={scanTemplate.language}
                isTargetGroupPublic={Boolean(group.config?.is_public)}
                showSettings={true}
                showAddParticipants={true}
                showDownloadFeedback={true}
                showDownloadScores={true}
            />
            <SearchInput groupId={group.id} companyId={company.id} />

            <div className={styles.group_table_container}>
                <div className={styles.group_table_container__content}>
                    <TargetGroupTable
                        scan={scanTemplate}
                        groupId={group.id}
                        companyId={company.id}
                        groupHasOptOutQuestionnaire={
                            !!group.config?.has_opt_out_questionnaire
                        }
                        participants={participantsData?.participants}
                        participantsSelected={participantsSelected}
                        hasNoParticipants={
                            participantsData?.participants.length === 0
                        }
                        isUsersInfoLoading={isFetchingParticipants}
                        onSelect={(userId: string, event: any) =>
                            onUserSelectChange(userId, event)
                        }
                        onSelectAll={() => onUserSelectChangeAll()}
                        isSuperAdmin={isSuperAdmin}
                        isBatchProcessLoading={isBatchProcessLoading}
                        participantIdsBeingBatchProcessed={getParticipantIdsToBatchProcess(
                            originalArgs?.operation,
                        )}
                    />
                    {!!participantsData &&
                        participantsData.participants.length >= perPage && (
                            <div className={styles.line_break} />
                        )}
                </div>
                <TargetGroupTableFooter
                    candidateCurrentPage={currentPage}
                    candidateTotalPages={participantsData?.totalPages ?? 1}
                    isBatchProcessLoading={isBatchProcessLoading}
                    isSuperAdmin={isSuperAdmin}
                    participantsSelected={participantsSelected}
                    setSelectedParticipants={setSelectedParticipants}
                    onSendResetPasswordEvent={() =>
                        showResetPasswordModal(true)
                    }
                    onSendReminderEvent={() => showReminderModal(true)}
                    onSendReportEvent={() => showReportModal(true)}
                    onRemoveCandidateEvent={() => showRemoveModal(true)}
                    onFullDeleteCandidateEvent={() =>
                        showFullDeleteCandidateModal(true)
                    }
                    onResetCandidateScanEvent={() =>
                        showResetCandidateScanModal(true)
                    }
                    setSearchParams={setSearchParams}
                />
            </div>

            <>
                <hr />
                <div className={styles.target_group_info_box__scan_name}>
                    <h2>Scan used by this group</h2>
                </div>
                {company && (
                    <TargetGroupScan
                        currentCompany={company}
                        assessment={scanTemplate}
                    />
                )}
                <hr />
            </>

            <div className={styles.box}>
                <div className={styles.target_group_info_box__scan_name}>
                    <h2>Baseline used in this group</h2>
                </div>
                {!!companyWorkPreferencesBaseline &&
                    !!companyWorkPreferencesBaseline.length && (
                        <WorkPreferenceBaseLineBarChart
                            data={formattedWorkPreferencesBaseline}
                        />
                    )}
                {(!companyWorkPreferencesBaseline ||
                    companyWorkPreferencesBaseline.length === 0) && (
                    <>
                        {isFetchingWorkPreferencesBaseline && (
                            <div className={styles.box__spinner}>
                                <ProgressSpinner />
                            </div>
                        )}
                        {!isFetchingWorkPreferencesBaseline && (
                            <p className={styles.box__description}>
                                No baseline is available for this group.
                            </p>
                        )}
                    </>
                )}
                <hr />
            </div>

            <div className={styles.box}>
                <h3>Delete group</h3>
                <p className={styles.box__description}>
                    Deleting this group is permanent.
                </p>
                <AppButton
                    isDisabled={participantsData?.participants.length !== 0}
                    onClick={() => {
                        showDeleteTargetGroupModal(true);
                    }}>
                    Delete group
                </AppButton>
            </div>

            <TargetGroupModals
                sendReminderEmails={sendReminderEmails}
                sendResetPasswordEmails={sendParticipantsResetPasswordEmails}
                sendReportEmails={sendReportEmails}
                removeCandidates={removeCandidates}
                fullDeleteCandidates={fullDeleteCandidates}
                resetCandidateScan={resetCandidateScan}
                shouldShowResetCandidateScanSuccessModal={
                    shouldShowResetCandidateScanSuccessModal
                }
                deleteTargetGroup={deleteEmptyTargetGroup}
                participantsSelected={participantsSelected}
                shouldShowInviteModal={shouldShowInviteModal}
                shouldShowResetPasswordModal={shouldShowResetPasswordModal}
                shouldShowReminderModal={shouldShowReminderModal}
                shouldShowReportModal={shouldShowReportModal}
                shouldShowRemoveModal={shouldShowRemoveModal}
                shouldShowFullDeleteCandidateModal={
                    shouldShowFullDeleteCandidateModal
                }
                shouldShowResetCandidateScanModal={
                    shouldShowResetCandidateScanModal
                }
                shouldShowDeleteTargetGroupModal={
                    shouldShowDeleteTargetGroupModal
                }
                onDiscardModal={discardModals}
            />
        </div>
    );
};

export default TargetGroupView;
